// Local-mode mocks for the Auth Logs page (/pam/auth-logs). Development only.
//
// Rows mirror ad-service's AuthEventDTO (internal/ad/models/dto/authlog_dto.go).
// Protocol values are the lowercase strings gateways store in
// did.auth_log.protocol. The SSH rows carry the session ids of the mock
// recordings in sshProxyMocks.js (9001 / 9002) as their correlationId, so the
// "Recording" link on those rows lands on a real mock recording.

const NOW = Date.now();
const ago = (ms) => NOW - ms;
const MIN = 60 * 1000;

const EVENTS = [
  { protocol: "ssh", principal: "aditya@authnull.com", realm: "web-01.prod", clientIp: "10.0.0.21", decision: "allow", reason: "mfa approved", correlationId: "d7bbae886dcfe00d", tsUnixMs: ago(6 * MIN) },
  { protocol: "kerberos", principal: "jdoe", realm: "CORP.LOCAL", clientIp: "10.1.4.17", decision: "allow", reason: "", correlationId: "dc01-6b1f0c2e-9a7d-4b8e-a1f3-2c5d7e9f0a11", tsUnixMs: ago(11 * MIN) },
  { protocol: "ntlm", principal: "jdoe", realm: "CORP.LOCAL", clientIp: "10.1.4.90", decision: "deny", reason: "bad password", upstreamErrorCode: 24, correlationId: "dc01-0e2a4c6b-8d1f-4a3e-b5c7-9e0f1a2b3c4d", tsUnixMs: ago(18 * MIN) },
  { protocol: "radius", principal: "msmith", realm: "vpn-edge-01", clientIp: "203.0.113.40", decision: "mfa_required", reason: "push sent", correlationId: "radius-5f0a7b21", tsUnixMs: ago(24 * MIN) },
  { protocol: "radius", principal: "msmith", realm: "vpn-edge-01", clientIp: "203.0.113.40", decision: "allow", reason: "mfa approved", correlationId: "radius-5f0a7b21", tsUnixMs: ago(23 * MIN) },
  { protocol: "ldap", principal: "svc-backup", realm: "CORP.LOCAL", clientIp: "10.1.8.3", decision: "allow", reason: "", correlationId: "dc02-7c3d1e5f-2a4b-4c6d-8e0f-1a3b5c7d9e2f", tsUnixMs: ago(37 * MIN) },
  { protocol: "ssh", principal: "oracle", realm: "db-01.prod", clientIp: "10.0.0.44", decision: "allow", reason: "policy: eu db oracle — everyone", correlationId: "3f91cc20ab7e4d18", tsUnixMs: ago(52 * MIN) },
  { protocol: "smb", principal: "jdoe", realm: "CORP.LOCAL", clientIp: "10.1.4.17", decision: "allow", reason: "", correlationId: "dc01-1f3e5d7c-9b0a-4e2d-a6c8-0b2d4f6a8c1e", tsUnixMs: ago(64 * MIN) },
  { protocol: "ssh", principal: "root", realm: "web-01.prod", clientIp: "198.51.100.9", decision: "deny", reason: "no policy covers this session", correlationId: "a01c9e7f55b24d03", tsUnixMs: ago(90 * MIN) },
].map((e) => ({ gatewayId: "gw-local-1", tenantId: 1, orgId: 1, upstreamErrorCode: 0, ...e }));

function filterEvents(body = {}) {
  const principal = String(body.principal || "").toLowerCase();
  return EVENTS.filter(
    (e) =>
      (!body.protocol || e.protocol === body.protocol) &&
      (!body.decision || e.decision === body.decision) &&
      (!principal || e.principal.toLowerCase().includes(principal)) &&
      (!body.excludeIntermediate || e.decision !== "mfa_required") &&
      (!body.sinceUnixMs || e.tsUnixMs >= body.sinceUnixMs) &&
      (!body.untilUnixMs || e.tsUnixMs <= body.untilUnixMs),
  ).sort((a, b) => b.tsUnixMs - a.tsUnixMs);
}

function timeline(events) {
  const bucket = 15 * MIN;
  const counts = new Map();
  events.forEach((e) => {
    const t = Math.floor(e.tsUnixMs / bucket) * bucket;
    counts.set(t, (counts.get(t) || 0) + 1);
  });
  return [...counts.entries()].sort((a, b) => a[0] - b[0]).map(([tsUnixMs, count]) => ({ tsUnixMs, count }));
}

// The SSH access log (PAM mode): a day of attempts, the chart and the counts.
const sshAttempt = (minsAgo, o) => {
  const t = Date.now() - minsAgo * MIN;
  const iso = (ms) => new Date(ms).toISOString();
  return {
    sessionId: `mock-${minsAgo}`,
    time: iso(t),
    live: false,
    recordings: [],
    gateway: { id: 1, name: "prod-bastion" },
    target: { account: "ubuntu", host: "10.2.14.21", endpointId: 1, hostname: "web-01.prod" },
    operator: { userId: 1, email: "aditya@authnull.com", keyFingerprint: "SHA256:tEPOgSstqqKAOL2T5jrNWlW7r66QRdY5pFsMsW4qcT8", srcIp: "10.0.0.21" },
    timeline: [
      { time: iso(t), event: "connect", label: "Connected to the gateway" },
      { time: iso(t + 2000), event: "push", label: "Push sent" },
    ],
    ...o,
  };
};
const SSH_ATTEMPTS = [
  sshAttempt(3, { outcome: "approved", live: true, decisionId: "dec-3", policy: { id: "p1", name: "web admins" }, recordings: [{ id: 101, sizeBytes: 20480 }] }),
  sshAttempt(42, { outcome: "approved", durationSec: 252, decisionId: "dec-42", policy: { id: "p1", name: "web admins" }, recordings: [{ id: 102, sizeBytes: 51200 }, { id: 103, sizeBytes: 2048 }] }),
  sshAttempt(60, { outcome: "approved", durationSec: 540, decisionId: "dec-60", policy: { id: "p9", name: "contractors" }, operator: { userId: 7, email: "sathyam@authnull.com", srcIp: "10.0.0.33" }, target: { account: "an-sathyam-2997", requested: "temp", credential: "temporary", host: "10.2.14.21", endpointId: 1, hostname: "web-01.prod" }, recordings: [{ id: 105, sizeBytes: 12000 }] }),
  sshAttempt(95, { outcome: "denied", reasonLabel: "Push declined", decisionId: "dec-95", target: { account: "root", host: "10.2.14.30", hostname: "db-01.prod" } }),
  sshAttempt(180, { outcome: "refused", reasonLabel: "No policy names this account", operator: { keyFingerprint: "SHA256:Zx1cV2bN3mA4sD5fG6hJ7kL8qW9eR0tY1uI2oP3aS4d", srcIp: "203.0.113.9" }, timeline: [] }),
  sshAttempt(410, { outcome: "no_answer", reasonLabel: "Push not answered in time" }),
  sshAttempt(700, { outcome: "failed", reasonLabel: "Target unreachable", durationSec: 0 }),
  sshAttempt(1300, { outcome: "approved", durationSec: 3725, recordings: [{ id: 104, sizeBytes: 90000 }] }),
];

function mockAccessLog(body = {}) {
  const bucketMs = Number(body.bucketMs) || 15 * MIN;
  const to = body.to ? Date.parse(body.to) : Date.now();
  const from = body.from ? Date.parse(body.from) : to - 24 * 60 * MIN;
  const op = String(body.operator || "").toLowerCase();
  const tg = String(body.target || "").toLowerCase();
  const base = SSH_ATTEMPTS.filter((a) => {
    const t = Date.parse(a.time);
    if (t < from || t > to) return false;
    if (op && !String(a.operator.email || "").toLowerCase().includes(op)) return false;
    if (tg && ![a.target.account, a.target.host, a.target.hostname].some((v) => String(v || "").toLowerCase().includes(tg))) return false;
    if (body.gatewayId && a.gateway.id !== Number(body.gatewayId)) return false;
    return true;
  });
  const counts = { attempts: base.length, approved: 0, denied: 0, refused: 0, noAnswer: 0, failed: 0, live: 0 };
  const key = { approved: "approved", denied: "denied", refused: "refused", no_answer: "noAnswer", failed: "failed" };
  const buckets = new Map();
  for (let t = Math.floor(from / bucketMs) * bucketMs; t <= to; t += bucketMs) {
    buckets.set(t, { tsUnixMs: t, approved: 0, denied: 0, refused: 0, noAnswer: 0, failed: 0 });
  }
  base.forEach((a) => {
    counts[key[a.outcome]] += 1;
    if (a.live) counts.live += 1;
    const b = buckets.get(Math.floor(Date.parse(a.time) / bucketMs) * bucketMs);
    if (b) b[key[a.outcome]] += 1;
  });
  const rows = base.filter((a) => !body.outcome || (body.outcome === "live" ? a.live : a.outcome === body.outcome));
  const limit = Number(body.limit) || 50;
  const start = Number(body.cursor) || 0;
  const page = rows.slice(start, start + limit);
  return { rows: page, counts, timeline: [...buckets.values()], bucketMs, nextCursor: start + limit < rows.length ? String(start + limit) : "" };
}

export const AUTH_LOG_MOCKS = [
  {
    pattern: "/ssh/accesslog",
    respond: (body) => mockAccessLog(body || {}),
  },
  {
    pattern: "/ad/getuserauthlog",
    respond: (body) => ({
      code: 200,
      status: "Success",
      message: "ok",
      events: filterEvents({ ...body, principal: "" }).filter(
        (e) => !body?.principal || e.principal === String(body.principal).toLowerCase(),
      ),
    }),
  },
  {
    pattern: "/ad/getauthbadsources",
    respond: (body) => {
      const byIp = new Map();
      EVENTS.filter((e) => e.decision === "deny" && (!body?.principal || e.principal === body.principal)).forEach((e) => {
        const s = byIp.get(e.clientIp) || { clientIp: e.clientIp, count: 0, lastTsUnixMs: 0 };
        s.count += 1;
        if (e.tsUnixMs > s.lastTsUnixMs) {
          s.lastTsUnixMs = e.tsUnixMs;
          s.lastReason = e.reason;
          s.lastErrorCode = e.upstreamErrorCode;
        }
        byIp.set(e.clientIp, s);
      });
      return { code: 200, status: "Success", message: "ok", sources: [...byIp.values()] };
    },
  },
  {
    pattern: "/ad/getauthlog",
    respond: (body) => {
      const all = filterEvents(body || {});
      const page = Math.max(1, Number(body?.page) || 1);
      const pageSize = Math.min(100, Math.max(1, Number(body?.pageSize) || 25));
      return {
        code: 200,
        status: "Success",
        message: "ok",
        total: all.length,
        page,
        pageSize,
        events: all.slice((page - 1) * pageSize, page * pageSize),
        timeline: timeline(all),
      };
    },
  },
];
